/**
 * AssetRadar — UI language (DEV-150, Lingui PoC).
 *
 * German is the source language of the code (see lingui.config.ts), but the
 * DISPLAY default is English: a user who never picked a language gets German
 * only when the browser lists German before any other supported language.
 *
 * Like the theme, the choice is a display setting and lives in localStorage —
 * never in the session. Catalogs are imported lazily, so the bundle carries
 * only the active language; `@lingui/vite-plugin` compiles the .po file on
 * import.
 *
 * Everything below works on the global `i18n` instance of `@lingui/core`. The
 * `I18nProvider` in main.tsx subscribes to it, so `activateLocale()` alone
 * re-renders every component that reads translations through `useLingui()`.
 */

import { i18n, type MessageDescriptor } from '@lingui/core';

export type AppLocale = 'de' | 'en';

export const LOCALES: readonly AppLocale[] = ['de', 'en'];

/** Shown when neither a stored choice nor the browser asks for German. */
export const DEFAULT_LOCALE: AppLocale = 'en';

export const LOCALE_STORAGE_KEY = 'craw_locale';

/**
 * Intl tag per UI language, for dates, numbers and collation. British English
 * keeps day-month order and the 24-hour clock that German users of an English
 * UI expect from a European product.
 */
const INTL_LOCALES: Readonly<Record<AppLocale, string>> = { de: 'de-DE', en: 'en-GB' };

function isLocale(value: unknown): value is AppLocale {
  return value === 'de' || value === 'en';
}

/** localStorage can throw (Safari private mode, disabled storage) — never fatal. */
function readStoredLocale(): AppLocale | null {
  try {
    const stored = window.localStorage.getItem(LOCALE_STORAGE_KEY);
    return isLocale(stored) ? stored : null;
  } catch {
    return null;
  }
}

/** First supported language in the browser's preference list, if any. */
function browserLocale(): AppLocale | null {
  const preferred = navigator.languages.length > 0 ? navigator.languages : [navigator.language];
  for (const tag of preferred) {
    const language = tag.toLowerCase().split('-')[0];
    if (isLocale(language)) return language;
  }
  return null;
}

/** Stored choice, then the browser, then English. */
export function initialLocale(): AppLocale {
  return readStoredLocale() ?? browserLocale() ?? DEFAULT_LOCALE;
}

/** The active UI language; the source language before anything was activated (tests). */
export function currentLocale(): AppLocale {
  return isLocale(i18n.locale) ? i18n.locale : 'de';
}

/** BCP 47 tag for `Intl` / `toLocaleString` calls of the active UI language. */
export function intlLocale(): string {
  return INTL_LOCALES[currentLocale()];
}

/** Load the catalog of `locale`, switch the UI to it and stamp `<html lang>`. */
export async function activateLocale(locale: AppLocale): Promise<void> {
  const { messages } = await import(`../locales/${locale}/messages.po`);
  i18n.loadAndActivate({ locale, locales: [INTL_LOCALES[locale]], messages });
  document.documentElement.lang = locale;
}

/** Explicit user choice: activate and remember it. */
export async function changeLocale(locale: AppLocale): Promise<void> {
  await activateLocale(locale);
  try {
    window.localStorage.setItem(LOCALE_STORAGE_KEY, locale);
  } catch {
    // Storage unavailable: the choice still applies to this tab.
  }
}

/**
 * A label table whose values are translated on every READ, not once at import.
 * Lets a module keep exporting `Record<K, string>` (e.g. ROLE_LABELS) without
 * touching its consumers: each property is a getter over `i18n._()`, so a
 * component re-rendered after a language switch reads the new language.
 */
export function translatedRecord<K extends string>(
  messages: Readonly<Record<K, MessageDescriptor>>,
): Readonly<Record<K, string>> {
  const record = {} as Record<K, string>;
  for (const key of Object.keys(messages) as K[]) {
    Object.defineProperty(record, key, { enumerable: true, get: () => i18n._(messages[key]) });
  }
  return record;
}
